import { motion } from 'framer-motion'
import { Cpu, Wifi, Plug, Layers, Zap, Shield, Clock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

const TechnicalCoreSection = () => {
  const features = [
    {
      icon: Cpu,
      title: 'Edge-First Processing',
      description: 'AI models run locally on ESP32/Raspberry Pi for <500ms latency and data privacy.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Wifi,
      title: 'Hybrid Connectivity',
      description: 'Uses LoRaWAN for deep-pit penetration and WiFi/4G for high-speed retail data.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Plug,
      title: 'Non-Disruptive',
      description: 'Our "Plug-and-Play" philosophy makes legacy systems smart without stopping production.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Layers,
      title: 'Scalability',
      description: 'Bridge technology ready to scale across the MENA region\'s legacy machines.',
      color: 'from-orange-500 to-red-500',
    },
  ]

  const stats = [
    { icon: Clock, value: '<500ms', label: 'Latency' },
    { icon: Shield, value: '100%', label: 'Data Privacy' },
    { icon: Zap, value: '99.9%', label: 'Uptime' },
    { icon: Cpu, value: 'Edge AI', label: 'Processing' },
  ]

  return (
    <section id="technology" className="py-20 bg-secondary/50 relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
      
      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            The Technical <span className="gradient-text">Core</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Edge AI and Hybrid Connectivity form the backbone of our platform
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.05 }}
            >
              <Card className="text-center">
                <CardContent className="p-6">
                  <stat.icon className="text-primary mx-auto mb-3" size={24} />
                  <div className="text-3xl font-display font-bold gradient-text mb-1">
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground text-sm">{stat.label}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5 }}
            >
              <Card className="h-full hover:border-primary/30 transition-all duration-300">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <feature.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Technical Schematic Placeholder */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
                  Unified Architecture
                </h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Our bridge technology seamlessly connects legacy infrastructure with modern AI capabilities
                </p>
              </div>
              
              {/* Schematic Visualization */}
              <div className="relative aspect-video bg-secondary/50 rounded-xl overflow-hidden border">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="grid grid-cols-3 gap-4 md:gap-8 p-8">
                    {/* Left Side - Legacy Systems */}
                    <div className="space-y-4">
                      {['Legacy Meters', 'Old Machines', 'Analog Systems'].map((item) => (
                        <motion.div
                          key={item}
                          className="bg-background border rounded-lg p-4 text-center text-muted-foreground text-sm"
                          whileHover={{ scale: 1.05, borderColor: 'hsl(var(--primary))' }}
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Center - Bridge Technology */}
                    <div className="flex flex-col items-center justify-center">
                      <motion.div
                        className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center glow-effect"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <Cpu className="text-white" size={40} />
                      </motion.div>
                      <p className="text-foreground font-semibold mt-4 text-center">Bridge Technology</p>
                    </div>
                    
                    {/* Right Side - Modern Output */}
                    <div className="space-y-4">
                      {['Real-time Analytics', 'AI Optimization', 'Cloud Dashboard'].map((item) => (
                        <motion.div
                          key={item}
                          className="bg-primary/10 border border-primary/30 rounded-lg p-4 text-center text-foreground text-sm"
                          whileHover={{ scale: 1.05 }}
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  )
}

export default TechnicalCoreSection
